import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import LoadingSpinner from '../../partials/LoadingSpinner';

import UpdateThresholdModal from './UpdateThresholdModal';
import DeleteThresholdModal from './DeleteThresholdModal';

import { H4, Panel, Box, Text, Button, Badge } from '@bigcommerce/big-design';
import { DeleteIcon, EditIcon, ReceiptIcon, AssignmentIcon, ChevronRightIcon, ChevronLeftIcon } from '@bigcommerce/big-design-icons';

import { thresholdsIndex } from '../../../redux/actions/thresholds';

const ThresholdList = ({ payload, activeAddress, thresholds, continuationToken, getThresholdsPage }) => {
  const [submitted, setSubmitted] = useState(false);
  const [activeThreshold, setActiveThreshold] = useState(null);
  const [showEditThresholdModal, setShowEditThresholdModal] = useState(false);
  const [showDeleteThresholdModal, setShowDeleteThresholdModal] = useState(false);

  const [page, setPage] = useState(0);

  useEffect(() => {
    getNextThresholdPage();
  }, []);

  useEffect(() => {
    setPage(0);
    getNextThresholdPage();
  }, [activeAddress]);


  const showEditModal = (threshold) => {
    setActiveThreshold(threshold);
    setShowEditThresholdModal(true);
  }

  const showDeleteModal = (threshold) => {
    setActiveThreshold(threshold);
    setShowDeleteThresholdModal(true);
  }

  const getPreviousThresholdsPage = () => {
    let previousContinuationToken = thresholds.thresholdsSearchStack[page - 2];
    getThresholdsPage(payload, activeAddress.id, previousContinuationToken, (page - 1));
    setPage(page - 1);
  }

  const getNextThresholdPage = () => {
    getThresholdsPage(payload, activeAddress.id, thresholds.thresholdsSearchStack[page], (page + 1));
    setPage(page + 1);
  }

  return(
    <>
      <Panel>
        <div className="full-width">
          <Box marginBottom="medium">
     
            <div className="flex-container">
              <div className="flex-1">
                <Button
                  onClick={() => getPreviousThresholdsPage()}
                  disabled={page === 1}
                  variant="secondary"
                  iconLeft={<ChevronLeftIcon />}
                >
                  Previous Thresholds
                </Button>

                <Button
                  onClick={() => getNextThresholdPage()}
                  disabled={thresholds.lastPage}
                  variant="secondary"
                  iconRight={<ChevronRightIcon />}
                >
                  Next Thresholds
                </Button>
              </div>
            </div>
            
            <br/>

            { thresholds.isFetching &&
              <LoadingSpinner statusText="Loading Thresholds" />
            }

            { thresholds.thresholds.length === 0 && !thresholds.isFetching &&
              <Box
                backgroundColor="warning10"
                border="box"
                borderRadius="normal"
                padding="medium"
              >
                <div className="text-center">
                  <Text>No thresholds created for this location. Add location thresholds to see them listed here.</Text>
                </div>
              </Box>
            }

            { thresholds.thresholds.length > 0 &&
                <div className="zebra-stripe-children">
                  { thresholds.thresholds.map(threshold =>
                    <lineitem>
                      <div className="has-hover sku-line">
                        <H4>{ threshold.variant_id ? 'Variant' : 'Product'} - { threshold?.product_data?.name || thresholds.relatedProducts[threshold.product_id]?.name }</H4>
                        <div className="flex-container">
                          <div className="left-15">
                            { threshold?.product_data?.primary_image && <img src={threshold?.product_data?.primary_image.url_tiny} /> }
                            { threshold?.variant_data?.imageUrl && <img src={threshold?.variant_data?.imageUrl} /> }
                            { !threshold?.variant_data?.imageUrl && thresholds.relatedProducts[threshold.product_id]?.primary_image && <img src={thresholds.relatedProducts[threshold.product_id]?.primary_image.url_tiny} /> }
                            { !threshold?.product_data?.primary_image && !threshold?.variant_data?.imageUrl && !thresholds.relatedProducts[threshold.product_id]?.primary_image && <Text>No image</Text>}
                          </div>
                          <div className="flex-2">
                            <div className="left-15">
                              <H4>Inventory Thresholds</H4>
                              <ul>
                                <li>Target Inventory: <Badge label={threshold.target_inventory || "Not set"} variant={threshold.target_inventory ? "success" : "warning"} /></li>
                                <li>Low Inventory: <Badge label={threshold.low_inventory || "Not set"} variant={threshold.low_inventory ? "success" : "warning"} /></li>
                                <li>Critical Inventory: <Badge label={threshold.critical_inventory || "Not set"} variant={threshold.critical_inventory ? "success" : "warning"} /></li>
                              </ul>
                            </div>
                          </div>


                          <div className="flex-2">
                            <H4>Item Details</H4>
                            <ul>
                              { threshold?.variant_data?.option_values && threshold?.variant_data?.option_values.map(option =>
                                  <li>
                                    <strong>{ option.option_display_name }</strong>: <Badge label={ option.label } variant="secondary" />
                                  </li>
                                )
                              }
                            </ul>
                          </div>
                          <div className="flex-1">
                            <div className="left-15">
                              <Button
                                onClick={() => showEditModal(threshold)}
                                variant="secondary"
                                disabled={thresholds.updatingThresholds.find(thresholdId => thresholdId === `${threshold.product_id}/${threshold.variant_id}`)}
                                isLoading={thresholds.updatingThresholds.find(thresholdId => thresholdId === `${threshold.product_id}/${threshold.variant_id}`)}
                                iconLeft={<EditIcon />}
                              >
                                Edit Thresholds
                              </Button>
                              <br/>
                              <br/>
                              <Button
                                onClick={() => showDeleteModal(threshold)}
                                variant="secondary"
                                actionType="destructive"
                                disabled={thresholds.updatingThresholds.find(thresholdId => thresholdId === `${threshold.product_id}/${threshold.variant_id}`)}
                                isLoading={thresholds.updatingThresholds.find(thresholdId => thresholdId === `${threshold.product_id}/${threshold.variant_id}`)}
                                iconLeft={<DeleteIcon />}
                              >
                                Remove Thresholds
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </lineitem>
                  )}
                </div>
              }
          </Box>
        </div>
      </Panel>

      <UpdateThresholdModal
        payload={payload}
        locationId={activeAddress.id}
        showUpdateThresholdModal={showEditThresholdModal}
        setShowUpdateThresholdModal={setShowEditThresholdModal}
        threshold={activeThreshold}
        thresholdName={activeThreshold?.product_data?.name || thresholds.relatedProducts[activeThreshold?.product_id]?.name}
      />

      <DeleteThresholdModal
        payload={payload}
        locationId={activeAddress.id}
        showDeleteThresholdModal={showDeleteThresholdModal}
        setShowDeleteThresholdModal={setShowDeleteThresholdModal}
        threshold={activeThreshold}
        thresholdName={activeThreshold?.product_data?.name || thresholds.relatedProducts[activeThreshold?.product_id]?.name}
      />
    </>
  )

}

const mapStateToProps = state => ({
  thresholds: state.thresholds,
  continuationToken: state.thresholds.thresholds?.continuationToken,
});

const mapDispatchToProps = (dispatch) => ({
  getThresholdsPage: (payload, locationId, continuationToken, page) => dispatch(thresholdsIndex(payload, locationId, continuationToken, page)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThresholdList);

